import React from "react"
import PropTypes from "prop-types"
import { Button, Col, Row, Badge, Alert, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap"

import { useActivityObjectivesTopics } from "../Hooks/useActivityObjectivesTopics"
import { useEvaluationDraft } from "../Hooks/useEvaluationDraft"
import { useSaveEvaluations } from "../Hooks/useSaveEvaluations"
import { useEvaluationMessaging } from "../Hooks/useEvaluationMessaging"
import LevelDropdown from "../../Grade/components/LevelDropdown"
import ButtonLoader from "../../../components/Common/ButtonLoader"

const TechnicalEvaluationForm = ({
    classId,
    idActivity,
    classEmployeeName,
    activityName: activityNameProp,
    evaluationLogic,
}) => {
    const {
        isLoading,
        withLoading,
        formReady,
        levels,
        activeEvent,
        allMembers,
        evaluationMembers,
        defaultLevelId,
        toggleExcludeMember,
        excludedMemberIds
    } = evaluationLogic

    const {
        objectives,
        topics,
        selectedObjective,
        selectedObjectiveId,
        selectedTopicId,
        handleSelectObjective,
        handleSelectTopic,
        handleNext,
        handlePrev,
        isLastTopicOfLastObjective,
        isFirstTopicOfFirstObjective,
        allTopicIds,
        topicMetaById,
    } = useActivityObjectivesTopics({ idActivity, withLoading })

    const {
        draftLevelsByTopicId,
        currentTopicLevels,
        dirtyCount: evaluationDirtyCount,
        handleLevelChange,
    } = useEvaluationDraft({
        idActivity,
        selectedTopicId,
        members: allMembers,
        excludedIds: excludedMemberIds,
        defaultLevelId,
        withLoading,
        activeEventId: activeEvent?.id,
    })

    const { saveAll: saveEvaluations } = useSaveEvaluations({
        idActivity,
        classId,
        members: allMembers,
        excludedIds: excludedMemberIds,
        draftLevelsByTopicId,
        allTopicIds,
        topicMetaById,
        defaultLevelId,
        levels,
        withLoading,
        activeEventId: activeEvent?.id,
        classEmployeeName: classEmployeeName || "",
        activityName: activityNameProp || "",
        eventName: activeEvent?.name || "",
    })

    const { sendEvaluationToMember, sendAllEvaluations, sendingStatus } = useEvaluationMessaging({
        idActivity,
        members: allMembers,
        excludedIds: excludedMemberIds,
        draftLevelsByTopicId,
        allTopicIds,
        topicMetaById,
        levels,
        withLoading,
        activeEventId: activeEvent?.id,
        activityName: activityNameProp || "",
    })

    // Placeholder for Gamification logic if needed in the future
    React.useEffect(() => {
        if (selectedTopicId && topicMetaById[selectedTopicId]) {
            // placeholder for gamification
        }
    }, [selectedTopicId, topicMetaById]);

    // ─── LOADING GATE ───────────────────────────────────────────────
    // formReady é false desde o primeiro frame (loadingKeys inicializa com true)
    // Isso garante ZERO flash de conteúdo parcial.
    if (!formReady) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center py-5">
                <div className="spinner-border text-primary mb-3" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
                    <span className="visually-hidden">Carregando...</span>
                </div>
                <p className="text-muted mb-0 font-size-14">Preparando avaliação...</p>
            </div>
        )
    }

    if (!activeEvent) {
        return (
            <Alert color="warning" className="mb-4 d-flex align-items-center" fade={false}>
                <i className="mdi mdi-alert-outline fs-4 me-3"></i>
                <div>
                    <h5 className="alert-heading font-size-14 mb-1">Período de avaliação fechado</h5>
                    <p className="mb-0 small">Consulte a gerência para iniciar um novo ciclo de avaliações técnicas.</p>
                </div>
            </Alert>
        )
    }
    // ─── FIM DO LOADING GATE ─────────────────────────────────────────

    return (
        <>
            <div className="mb-4 bg-light p-2 p-md-3 rounded border">
                <Row className="g-2 align-items-end">
                    <Col xs="12" md="5">
                        <label className="form-label fw-bold mb-1 small text-uppercase">Objetivo</label>
                        <UncontrolledDropdown className="w-100" disabled={objectives.length === 0}>
                            <DropdownToggle caret className="w-100 text-start bg-white text-dark border d-flex justify-content-between align-items-center">
                                <div className="d-flex w-100 align-items-center text-truncate pe-2">
                                    {(() => {
                                        if (objectives.length === 0) return "Nenhum objetivo";
                                        const o = objectives.find(x => String(x.id) === selectedObjectiveId) || objectives[0];
                                        if (!o) return "Selecione...";
                                        const idx = objectives.findIndex(x => x.id === o.id);
                                        const label = `${idx + 1}. ` + (o.name || o.title || "Objetivo");
                                        return <span className="text-truncate">{label}</span>;
                                    })()}
                                </div>
                            </DropdownToggle>
                            <DropdownMenu className="w-100 shadow-lg border-0" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {objectives.length === 0 ? (
                                    <DropdownItem disabled>Nenhum objetivo</DropdownItem>
                                ) : (
                                    objectives.map((o, idx) => {
                                        const label = `${idx + 1}. ` + (o.name || o.title || "Objetivo");
                                        return (
                                            <DropdownItem key={o.id} onClick={() => handleSelectObjective(String(o.id))} active={String(o.id) === selectedObjectiveId} className="py-2 border-bottom">
                                                <div className="d-flex align-items-center justify-content-between w-100">
                                                    <span className="text-wrap me-3" style={{ maxWidth: '90%' }}>{label}</span>
                                                </div>
                                            </DropdownItem>
                                        )
                                    })
                                )}
                            </DropdownMenu>
                        </UncontrolledDropdown>
                    </Col>

                    <Col xs="12" md="6">
                        <label className="form-label fw-bold mb-1 small text-uppercase">Tópico</label>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <UncontrolledDropdown className="w-100" disabled={!selectedObjective || topics.length === 0}>
                                <DropdownToggle caret className="w-100 text-start bg-white text-dark border d-flex justify-content-between align-items-center">
                                    <div className="d-flex w-100 align-items-center text-truncate pe-2">
                                        {(() => {
                                            if (topics.length === 0) return "Nenhum tópico";
                                            const t = topics.find(x => String(x.id) === selectedTopicId) || topics[0];
                                            if (!t) return "Selecione...";
                                            const idx = topics.findIndex(x => x.id === t.id);
                                            const label = `${idx + 1}. ` + (t.name || t.description || "Tópico");
                                            return (
                                                <div className="d-flex align-items-center gap-2 w-100 me-2" style={{ minWidth: 0 }}>
                                                    <span className="text-truncate">{label}</span>
                                                    {t.isFundamental && <Badge color="warning" className="text-dark font-size-10 px-2 shadow-sm rounded-pill flex-shrink-0">FUND.</Badge>}
                                                    {Number(t.xp) > 0 && <Badge color="primary" className="font-size-10 px-2 shadow-sm rounded-pill flex-shrink-0">+{t.xp} XP</Badge>}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </DropdownToggle>
                                <DropdownMenu className="w-100 shadow-lg border-0" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    {topics.length === 0 ? (
                                        <DropdownItem disabled>Nenhum tópico</DropdownItem>
                                    ) : (
                                        topics.map((t, idx) => {
                                            const label = `${idx + 1}. ` + (t.name || t.description || "Tópico");
                                            return (
                                                <DropdownItem key={t.id} onClick={() => handleSelectTopic(String(t.id))} active={String(t.id) === selectedTopicId} className="py-2 border-bottom">
                                                    <div className="d-flex align-items-center justify-content-between w-100">
                                                        <span className="text-wrap me-3" style={{ maxWidth: '75%' }}>{label}</span>
                                                        <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                                            {t.isFundamental && <Badge color="warning" className="text-dark font-size-10 px-2 py-1 shadow-sm rounded-pill">FUND.</Badge>}
                                                            {Number(t.xp) > 0 && <Badge color="primary" className="font-size-10 px-2 py-1 shadow-sm rounded-pill">+{t.xp} XP</Badge>}
                                                        </div>
                                                    </div>
                                                </DropdownItem>
                                            )
                                        })
                                    )}
                                </DropdownMenu>
                            </UncontrolledDropdown>
                        </div>
                    </Col>

                    <Col xs="12" md="1">
                        <div className="d-flex align-items-center gap-2 justify-content-md-end">
                            <Button color="primary" outline size="sm" onClick={handlePrev} disabled={objectives.length === 0 || isFirstTopicOfFirstObjective}>
                                <i className="mdi mdi-chevron-left" />
                            </Button>
                            <Button color="primary" outline size="sm" onClick={handleNext} disabled={objectives.length === 0 || isLastTopicOfLastObjective}>
                                <i className="mdi mdi-chevron-right" />
                            </Button>
                        </div>
                    </Col>
                </Row>
                {isLoading("prefill") && (
                    <div className="mt-2 small text-muted">
                        <i className="mdi mdi-loading mdi-spin me-1" /> Sincronizando dados anteriores...
                    </div>
                )}
            </div>

            <div className="members-list">
                <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                    <h6 className="mb-0 fw-bold">Alunos para Avaliação</h6>
                    <Badge color="dark" pill>{evaluationMembers.length}</Badge>
                </div>

                {evaluationMembers.length === 0 ? (
                    <div className="text-center py-5 border rounded bg-light border-dashed">
                        <i className="mdi mdi-account-search-outline fs-1 text-muted opacity-50 mb-3 d-block"></i>
                        <p className="text-muted mb-0 font-size-13">Nenhum aluno apto encontrado.</p>
                    </div>
                ) : (
                    <div className="d-flex flex-column gap-3">
                        {evaluationMembers.map(member => {
                            const sId = String(member.id)
                            const currentLevel = currentTopicLevels?.[sId] || defaultLevelId || ""

                            return (
                                <div key={sId} className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 p-3 bg-white rounded border shadow-sm mb-2">
                                    <div className="d-flex align-items-center gap-3 w-100 w-md-auto">
                                        <div className="flex-shrink-0">
                                            {member.photo ? (
                                                <img
                                                    src={member.photo}
                                                    alt={member.name}
                                                    className="rounded-circle border"
                                                    style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <div className="rounded-circle border bg-light d-flex align-items-center justify-content-center shadow-sm" style={{ width: '48px', height: '48px' }}>
                                                    <i className="mdi mdi-account fs-3" style={{ color: '#9e9e9e' }}></i>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-grow-1">
                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                <div className="fw-bold text-dark fs-5">{member.name}</div>
                                                {member.memberNumber && <span className="text-muted small">#{member.memberNumber}</span>}
                                            </div>
                                            <div className="d-flex gap-2 align-items-center mt-1">
                                                {member.status && (
                                                    <Badge color={
                                                        member.status === 'active' ? 'success' :
                                                            member.status === 'suspended' ? 'warning' : 'secondary'
                                                    } className="border px-2 text-capitalize">
                                                        {member.status === 'active' ? 'Ativo' : member.status}
                                                    </Badge>
                                                )}
                                                {member.tag && <Badge color="light" className="text-muted border d-none d-md-inline-block">{member.tag}</Badge>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-flex flex-column flex-md-row align-items-center gap-2 gap-md-3 w-100 w-md-auto ms-auto">
                                        <div className="w-100 w-md-auto" style={{ minWidth: '160px' }}>
                                            <div className="d-md-none small text-muted text-uppercase fw-bold mb-1 text-center">
                                                Atribuir Nível
                                            </div>
                                            <LevelDropdown
                                                memberId={sId}
                                                currentLevel={currentLevel}
                                                onLevelChange={handleLevelChange}
                                                levels={levels}
                                                disabled={!selectedTopicId || isLoading("prefill") || !activeEvent}
                                                fullWidth={true}
                                                className="w-100 w-md-auto"
                                                toggleClassName="w-100 py-2 py-md-1"
                                            />
                                        </div>

                                        <div className="d-flex align-items-center gap-2 w-100 w-md-auto justify-content-center">
                                            <Button
                                                color="success"
                                                outline
                                                className="d-flex align-items-center justify-content-center shadow-sm flex-grow-1 flex-md-grow-0"
                                                style={{ height: '38px', minWidth: '42px' }}
                                                onClick={() => sendEvaluationToMember(member)}
                                                title="Enviar WhatsApp"
                                            >
                                                <i className="mdi mdi-whatsapp fs-4" />
                                                <span className="ms-2 d-md-none fw-bold">Enviar</span>
                                            </Button>

                                            <Button
                                                outline
                                                color="danger"
                                                className="d-flex align-items-center justify-content-center flex-grow-1 flex-md-grow-0 border-0"
                                                style={{ height: '38px', minWidth: '42px' }}
                                                onClick={() => toggleExcludeMember(sId)}
                                                title="Remover da lista"
                                            >
                                                <i className="mdi mdi-close-circle-outline fs-4" />
                                                <span className="ms-2 d-md-none fw-bold">Remover</span>
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}

                {excludedMemberIds.size > 0 && (
                    <div className="mt-4">
                        <div className="d-flex align-items-center justify-content-between mb-2 px-1">
                            <h6 className="small fw-bold text-muted text-uppercase mb-0">
                                <i className="mdi mdi-eye-off-outline me-1"></i>
                                Alunos Ocultos ({excludedMemberIds.size})
                            </h6>
                            <Button
                                color="link"
                                size="sm"
                                className="p-0 text-decoration-none small"
                                onClick={() => {
                                    excludedMemberIds.forEach(id => toggleExcludeMember(id))
                                }}
                            >
                                Restaurar Todos
                            </Button>
                        </div>

                        <div className="d-flex flex-column gap-2 bg-soft-light p-2 rounded border border-dashed">
                            {allMembers
                                .filter(c => excludedMemberIds.has(String(c.id)))
                                .map(member => (
                                    <div key={member.id} className="d-flex align-items-center justify-content-between p-2 bg-white rounded border-bottom shadow-sm" style={{ opacity: 0.8 }}>
                                        <div className="d-flex align-items-center gap-2">
                                            {member.photo ? (
                                                <img
                                                    src={member.photo}
                                                    alt={member.name}
                                                    className="rounded-circle border"
                                                    style={{ width: '24px', height: '24px', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <div className="rounded-circle border bg-light d-flex align-items-center justify-content-center shadow-sm" style={{ width: '24px', height: '24px' }}>
                                                    <i className="mdi mdi-account" style={{ fontSize: '14px', color: '#9e9e9e' }}></i>
                                                </div>
                                            )}
                                            <span className="small fw-medium text-muted">{member.name}</span>
                                        </div>
                                        <Button
                                            color="primary"
                                            outline
                                            size="xs"
                                            className="py-0 px-2"
                                            style={{ fontSize: '0.65rem', height: '20px' }}
                                            onClick={() => toggleExcludeMember(member.id)}
                                        >
                                            Restaurar
                                        </Button>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}

                <div className="d-flex justify-content-end mt-4 pt-3 border-top gap-2">
                    <Button
                        color="success"
                        outline
                        onClick={sendAllEvaluations}
                        disabled={isLoading("saveAll") || objectives.length === 0 || !activeEvent || evaluationMembers.length === 0}
                        className="px-4 shadow-sm d-flex align-items-center"
                    >
                        <i className="mdi mdi-whatsapp me-2" /> Enviar Todos
                    </Button>
                    <ButtonLoader
                        color="success"
                        onClick={saveEvaluations}
                        loading={isLoading("saveAll")}
                        disabled={isLoading("saveAll") || objectives.length === 0 || evaluationDirtyCount === 0 || !activeEvent}
                        className="px-4 shadow-sm"
                    >
                        Salvar
                    </ButtonLoader>
                </div>
            </div>

            {sendingStatus.loading && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999 }}>
                    <div className="bg-white rounded shadow-lg p-4" style={{ maxWidth: '400px', width: '90%' }}>
                        <div className="text-center mb-4">
                            <div className="avatar-md mx-auto mb-3">
                                <span className="avatar-title rounded-circle bg-soft-success text-success font-size-24">
                                    <i className="mdi mdi-whatsapp"></i>
                                </span>
                            </div>
                            <h5 className="font-size-16 fw-bold mb-1">Enviando Avaliações</h5>
                            <p className="text-muted mb-0">Por favor, aguarde...</p>
                        </div>

                        <div className="mb-3">
                            <div className="d-flex justify-content-between font-size-13 mb-1">
                                <span className="fw-medium">{sendingStatus.step}</span>
                                <span>{sendingStatus.progress}%</span>
                            </div>
                            <div className="progress progress-sm rounded-pill">
                                <div
                                    className="progress-bar bg-success"
                                    role="progressbar"
                                    style={{ width: `${sendingStatus.progress}%`, transition: 'width 0.3s ease' }}
                                    aria-valuenow={sendingStatus.progress}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                ></div>
                            </div>
                        </div>

                        {sendingStatus.currentMemberName && (
                            <div className="text-center small text-muted mt-2">
                                Processando: <strong>{sendingStatus.currentMemberName}</strong>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}

TechnicalEvaluationForm.propTypes = {
    classId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    idActivity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    classEmployeeName: PropTypes.string,
    activityName: PropTypes.string,
    evaluationLogic: PropTypes.object
}

export default TechnicalEvaluationForm
